<template>
    <div class="ImportValidation">
        <WCard>
            <div class="Header flex flex-wrap row-stretch-center">
                <div class="Notifications mb-1">
                    <FadeTransition mode="out-in">
                        <ListStatus
                            v-if="importData"
                            v-bind="importData"
                            class="flex row-left-center"
                        />
                    </FadeTransition>
                </div>
                <div class="Actions">
                    <WButton
                        v-if="!isBlocked"
                        :disabled="isLoading"
                        :loading="isLoading"
                        outline
                        color="accent-blue"
                        class="ml-1"
                        @click="handleRequestModal(modals.CANCEL)"
                    >
                        {{ $t('imports.create.cancel') }}
                    </WButton>
                    <WButton
                        :disabled="!!(importData || {}).numErrors || isLoading"
                        :loading="isLoading"
                        color="primary"
                        class="ml-1"
                        @click="saveImport"
                    >
                        {{ $t('imports.create.save') }}
                    </WButton>
                </div>
            </div>

            <div
                v-if="issues"
                class="Notifications"
            >
                <template v-for="type in issuesTypes">
                    <FadeTransition
                        v-if="(issuesByType[type] || []).length"
                        :key="`issues-${type}`"
                        mode="out-in"
                    >
                        <ImportNotifications
                            :notifications="issuesByType[type]"
                            :type="type"
                            collapsable
                            :class="{ 'mb-1': grid }"
                        />
                    </FadeTransition>
                </template>
            </div>

            <div class="Body">
                <div
                    v-if="entityTabs.length > 1"
                    class="EntityTabs flex row-left-center mb-1"
                >
                    <WButton
                        v-for="tab in entityTabs"
                        :key="tab.entity"
                        :color="activeEntity === tab.entity ? 'primary' : 'secondary'"
                        :outline="activeEntity !== tab.entity"
                        size="sm"
                        class="mr-1"
                        @click="handleEntityTab(tab.entity)"
                    >
                        {{ tab.label }} ({{ tab.rows }} rows)
                    </WButton>
                </div>
                <DataGrid
                    v-if="grid"
                    ref="dataGrid"
                    :loading="isLoading"
                    v-bind="grid"
                    class="DataGrid"
                    @onCellDoubleClicked="handleClickCell"
                />
            </div>
        </WCard>

        <ModalRequest
            :opened="isModalRequest"
            :title="isModalRequest ? $t(`imports.create.modals.${modal}.text`) : ''"
            :cancelText="$t('general.cancel')"
            :okText="$t('general.accept')"
            :loading="isLoading"
            @onAccept="handleAction"
            @close="handleCleanModal"
        />

        <ModalIssues
            :opened="modal === modals.UPDATE_ISSUES"
            :importId="id"
            :entity="activeEntity"
            :columns="(grid || {}).columns"
            v-bind="node"
            @onResolve="handleResolveIssue"
            @onCancel="handleCleanModal"
        />

        <ModalCell
            :opened="modal === modals.UPDATE_CELL"
            :importId="id"
            :entity="activeEntity"
            v-bind="node"
            :selectGroups="(grid || {}).selectGroups"
            @onResolve="handleResolveIssue"
            @onCancel="handleCleanModal"
        />

        <ModalRequest
            :opened="modal === modals.REMOVE_ROW"
            :title="$tc('imports.modals.deleteRows.text', rowsSelected, { count: rowsSelected })"
            :cancelText="$t('actions.cancel')"
            :okText="$t('actions.confirm')"
            :loading="isEditing"
            preventDefault
            @close="handleCleanModal"
            @onAccept="removeImportRows"
        />
    </div>
</template>

<script>
import { noop } from 'lodash'
import {
    Api, apiRequest, rethrowError, notifyError,
} from '@/api'
import { entityMixin } from '@/mixins'
import { DataGrid, ModalRequest, ListStatus } from '@/components'
import { STATUS } from '@/entities/imports'
import { ImportNotifications } from '../components'
import { ModalIssues, ModalCell } from './components'
import Grid from './grid'

const MODALS = {
    FORCE: 'FORCE',
    CANCEL: 'CANCEL',
    UPDATE_ISSUES: 'UPDATE_ISSUES',
    UPDATE_CELL: 'UPDATE_CELL',
    REMOVE_ROW: 'REMOVE_ROW',
}

const ISSUES_TYPES = {
    ERROR: 'error',
    WARNING: 'warning',
    INFO: 'info',
    SUCCESS: 'success',
}

const EDIT_ALL_FIELDS = true

export default {
    name: 'ImportValidation',
    components: {
        ListStatus,
        ImportNotifications,
        DataGrid,
        ModalRequest,
        ModalIssues,
        ModalCell,
    },
    mixins: [entityMixin],
    metaInfo () {
        return {
            title: this.$t('admin.navigation.imports.create'),
        }
    },
    entity () {
        return {
            breadcrumbs: [
                {
                    key: 'AdminImportsList',
                    label: this.$t('imports.create.title'),
                },
                {
                    key: 'AdminImportsPreview',
                    label: this.$t('imports.steps.validation'),
                },
            ],
        }
    },
    data () {
        return {
            grid: undefined,
            importData: undefined,
            isLoading: false,

            modal: null,
            modals: MODALS,

            issues: undefined,
            issuesTypes: Object.values(ISSUES_TYPES),

            node: null,
            isEditing: false,

            // Multi-entity support
            entityTabs: [],
            entityGrids: {},
            activeEntity: null,
        }
    },
    computed: {
        isModalRequest () {
            return [this.modals.FORCE, this.modals.CANCEL].includes(this.modal)
        },
        isBlocked () {
            const { status } = this.importData || {}
            return [STATUS.cancelled, STATUS.failed].includes(status)
        },
        issuesByType () {
            const KEY_TYPE = 'level'
            return this.issues.reduce((acc, item) => ({ ...acc, ...({ [item[KEY_TYPE]]: [...(acc[item[KEY_TYPE]] || []), item] }) }), {})
        },
        rowsSelected () {
            return ((this.node || {}).rows || []).length || 0
        },
    },
    beforeMount () {
        const { id } = this.$route.params || {}
        this.id = id
    },
    mounted () {
        if ([undefined, null].includes(this.id)) this.$router.back()
        else this.getImport(this.id)
    },
    methods: {
        contextMenu ({ node, api, column }) {
            const rows = api.getSelectedNodes()
            const fieldKey = column && column.getId()
            const renderIcon = icon => `<i class="WIcon icon-${icon} ag-icon" />`

            return [
                'copy',
                'paste',
                'separator',
                {
                    name: this.$t('entities.actions.delete'),
                    action: () => this.handleRequestModal(MODALS.REMOVE_ROW, { fieldKey, rows }),
                    icon: renderIcon('trash'),
                },
                'separator',
                {
                    name: this.$t('entities.actions.export'),
                    action: () => api.exportDataAsCsv({ columnSeparator: '\t' }),
                    icon: renderIcon('download'),
                },
            ]
        },
        /**
         * Modals
         */
        handleAction () {
            (({
                [this.modals.CANCEL]: this.cancelImport,
                [this.modals.FORCE]: this.handleForce,
            })[this.modal] || noop)()
        },
        handleRequestModal (modal, node) {
            this.node = node
            if (modal) this.modal = modal
        },
        handleCleanModal () {
            this.modal = null
        },
        /**
         * Cells
         */
        handleClickCell ({ node, column }) {
            const { issuesCount } = node.data
            const { status } = this.importData
            const row = { row: node, col: column.colDef }

            if (![STATUS.failed, STATUS.cancelled].includes(status)) {
                if (issuesCount) this.handleRequestModal(this.modals.UPDATE_ISSUES, row)
                if (EDIT_ALL_FIELDS && !issuesCount) this.handleRequestModal(this.modals.UPDATE_CELL, row)
            }
        },
        handleResolveIssue ({ row, issues }) {
            const { columns } = this.grid
            const data = Grid.normalizeRow({ row, columns, issues: Grid.normalizeIssues(issues) })
            this.node.row.setData(data)

            // The edit endpoint returns the updated import: refresh the
            // header counters, which also gate the Save button.
            if (issues.import) this.importData = { ...this.importData, ...issues.import }

            if (!data.issuesCount) {
                this.handleCleanModal()
                this.node = null
            }
        },
        handleForce () {
            this.saveImport({ force: true })
        },
        handleError () {
            notifyError(this.$t('imports.create.modals.NOT_FOUND'))()
            this.$router.push({ name: 'AdminImportsList' })
        },
        /**
         * Multi-entity tabs
         */
        handleEntityTab (entity) {
            if (entity === this.activeEntity) return
            this.keepActiveEntityRows()
            this.activeEntity = entity
            this.grid = this.entityGrids[entity] || null
        },
        keepActiveEntityRows () {
            // Edits and deletes only update the live grid: save its rows back,
            // or coming back to this tab would show them as first loaded.
            const { gridApi } = this.$refs.dataGrid || {}
            const entityGrid = this.entityGrids[this.activeEntity]
            if (!gridApi || !entityGrid) return

            const rows = []
            gridApi.forEachNode(({ data }) => rows.push(data))
            this.entityGrids[this.activeEntity] = { ...entityGrid, rows }
        },
        /**
         * Datamanager
         */
        fetchData ({ data }) {
            const {
                preview, previews, import: importData, globalIssues, rowIssues, cellIssues,
            } = data
            const issues = Grid.normalizeIssues({ globalIssues, rowIssues, cellIssues })

            if (previews && previews.length) {
                // Multi-entity: build a grid for each entity tab
                this.entityTabs = previews.map(({ entity, label, preview: entityPreview }) => ({
                    entity,
                    label,
                    rows: (entityPreview && entityPreview.rows) ? entityPreview.rows.length : 0,
                }))

                previews.forEach(({ entity, preview: entityPreview }) => {
                    if (entityPreview) {
                        this.entityGrids[entity] = {
                            ...Grid.normalizeGrid({ data: entityPreview, issues }),
                            contextMenu: this.contextMenu,
                        }
                    }
                })

                // Activate first tab
                const firstEntity = this.entityTabs[0].entity
                this.activeEntity = firstEntity
                this.grid = this.entityGrids[firstEntity] || null
                this.issues = issues.global
            } else if (preview) {
                this.grid = {
                    ...Grid.normalizeGrid({ data: preview, issues }),
                    contextMenu: this.contextMenu,
                }
                this.issues = issues.global
            } else {
                this.issues = [...issues.global, ...issues.row, ...issues.cell]
            }

            this.importData = importData
        },
        /**
         * Requests
         */
        async getImport (id) {
            this.isLoading = true
            apiRequest(Api().imports.getSource(id))
                .then(this.fetchData)
                .catch(this.handleError)
                .finally(() => (this.isLoading = false))
        },
        async cancelImport () {
            this.isLoading = true
            apiRequest(Api().imports.cancel(this.id))
                .then(() => {
                    this.$router.push({ name: 'AdminImportsList' })
                    this.handleCleanModal()
                })
                .catch(rethrowError(notifyError()))
                .finally(() => (this.isLoading = false))
        },
        async saveImport (params = {}) {
            this.isLoading = true
            apiRequest(Api().imports.save(this.id, params))
                .then(() => {
                    this.$router.push({ name: 'AdminImportCreatePreview' })
                    this.handleCleanModal()
                })
                .catch(rethrowError(notifyError()))
                .finally(() => (this.isLoading = false))
        },
        async removeImportRows () {
            const { rows } = this.node
            // eslint-disable-next-line dot-notation
            const rowIds = rows.map(({ data }) => data['_inputRowId']).filter(id => !!id)
            if (!rowIds.length) {
                this.handleCleanModal()
                return
            }

            this.isEditing = true
            apiRequest(Api().imports.deleteSource(this.id, {
                rowIds,
                ...(this.activeEntity ? { entity: this.activeEntity } : {}),
            }))
                .then(({ data }) => {
                    rows.map(this.$refs.dataGrid.removeRow)
                    if (data && data.import) this.importData = { ...this.importData, ...data.import }
                    const tab = this.entityTabs.find(({ entity }) => entity === this.activeEntity)
                    if (tab) tab.rows -= rows.length
                })
                .catch(rethrowError(notifyError()))
                .finally(() => {
                    this.handleCleanModal()
                    this.isEditing = false
                })
        },
    },
}
</script>
<style lang="scss" scoped>
.ImportValidation {
    .WCard {
        @include admin-screen-height(54px);
    }
}

.ListStatus {
    &::v-deep .Group {
        display: flex;
    }
    &::v-deep .Group + .Group, &::v-deep .Status + .Status {
        margin-left: 20px;
    }
}

.EntityTabs {
    padding: 4px 0;
    border-bottom: 1px solid #e0e0e0;
}

.DataGrid {
	width: 100%;
	@include admin-screen-height(138px);
}
</style>
